import * as React from "react";
import { html, i18nKeys, t } from "../../i18n/i18n";
import { Throbber } from "../Throbber";
import { History } from "history";
import { getLoginService } from "../../login-service/LoginService";
import { getErrorMessage } from "../../login-service/LoginServiceErrors";
import { BigLogo } from "../BigLogo";
import trackAction from "../../libs/analytics/SigninPageAnalytics";
import {
  Button,
  ButtonsContainer,
  ButtonsRow,
  TextInput,
  TextItem,
  typeCompact24Display,
  Window,
  WindowBody,
  WindowVerticalLayout
} from "@traceair/tds";
import { emailRegExp } from "./SigninPage";
import {
  descriptionStyle,
  greetingStyle,
  headerStyle,
  windowStyle
} from "./styles";
import { getContactUsLink } from "../../helpers";
import { style } from "typestyle";

interface IRecoveryPageProps {
  history: History;
  refererUrl: string;
  defaultEmail: string;
}

interface IRecoveryPageState {
  pending: boolean;
  error?: string;
  success: boolean;
  email: string;
  emailWarningMessage: string;
  warningMessageTitle: any;
  warningMessageSubTitle?: any;
}

const getContactUsText = () => {
  return html(
    `${t(i18nKeys.ifNeedHelp)} ${getContactUsLink(t(i18nKeys.contactUs))}.`
  );
};

export class RecoveryPage extends React.Component<
  IRecoveryPageProps,
  IRecoveryPageState
> {
  constructor(props: IRecoveryPageProps) {
    super(props);
    this.state = {
      pending: false,
      error: undefined,
      success: false,
      email: this.props.defaultEmail || "",
      emailWarningMessage: "",
      warningMessageTitle: "",
      warningMessageSubTitle: ""
    };
    trackAction("Recovery page is opened");
  }

  validateEmail(email: string) {
    this.setState({ emailWarningMessage: "" });

    if (!email) {
      this.setState({ emailWarningMessage: t(i18nKeys.emailRequired) });
      return false;
    }

    if (!emailRegExp.test(email)) {
      this.setState({ emailWarningMessage: t(i18nKeys.incorrectEmail) });
      return false;
    }

    return true;
  }

  handleCancelClick = () => {
    this.props.history.push("/");
  };

  handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    this.setState({ pending: true });
    const { email } = this.state;
    const isEmailCorrect = this.validateEmail(email);
    if (!isEmailCorrect) {
      this.setState({ pending: false });
      return;
    }
    const { refererUrl } = this.props;
    getLoginService()
      .requestPasswordRecovery(email, refererUrl)
      .catch(error => {
        const { title, subTitle } = getErrorMessage(error);
        this.setState({
          warningMessageTitle: title,
          warningMessageSubTitle: subTitle,
          pending: false,
          success: false
        });
        throw error;
      })
      .then(() => {
        this.setState({
          warningMessageTitle: "",
          warningMessageSubTitle: "",
          pending: false,
          success: true,
          error: undefined
        });
      });
  };

  render() {
    const { pending, success } = this.state;

    return (
      <Window className={windowStyle} header={this.renderHeader()}>
        <WindowBody>
          <form onSubmit={this.handleSubmit}>
            {!success && (
              <Throbber pending={pending}>
                <WindowVerticalLayout>
                  {this.renderEmailInput()}
                </WindowVerticalLayout>
              </Throbber>
            )}
            {this.renderFooter()}
          </form>
        </WindowBody>
      </Window>
    );
  }

  renderHeader = () => {
    const { success } = this.state;
    return (
      <div className={headerStyle}>
        <BigLogo />
        {success ? this.renderSuccessText() : this.renderGreeting()}
      </div>
    );
  };

  renderGreeting = () => {
    const { error, success } = this.state;
    if (success || error) {
      return null;
    }
    return (
      <div className={greetingStyle}>
        <TextItem
          text={t(i18nKeys.requestPasswordRecoveryGreeting)}
          style={typeCompact24Display}
        />
        <div className={descriptionStyle}>
          {t(i18nKeys.requestPasswordRecoveryGreeting2)}
        </div>
      </div>
    );
  };

  renderSuccessText = () => {
    const { email } = this.state;

    return (
      <div className={successTextContainerStyle}>
        <TextItem
          text={t(i18nKeys.requestPasswordRecoverySuccessTitle)}
          style={typeCompact24Display}
        />

        <TextItem
          text={`${t(i18nKeys.requestPasswordRecoverySuccessSubtitle1)} ${email} ${t(
            i18nKeys.requestPasswordRecoverySuccessSubtitle2
          )}`}
          style={descriptionStyle}
        />

        <div className={descriptionStyle}>{getContactUsText()}</div>
      </div>
    );
  };

  renderEmailInput() {
    const { email, emailWarningMessage } = this.state;

    const onChange = (email: string) => {
      this.setState({ email });
    };
    return (
      <TextInput
        label={"Email"}
        value={email}
        onChange={onChange}
        placeholder={t(i18nKeys.emailPlaceholder)}
        isWarning={!!emailWarningMessage}
        warningMessage={emailWarningMessage}
        onBlur={() => this.validateEmail(email)}
        hidePlaceholderOnFocus
      />
    );
  }

  renderFooter() {
    const { success, email } = this.state;

    if (success) {
      return (
        <ButtonsContainer className={successButtonContainerStyle}>
          <Button
            style="secondary"
            text={t(i18nKeys.cancelRecover)}
            onClick={this.handleCancelClick}
          />
        </ButtonsContainer>
      );
    }

    return (
      <div className={buttonsContainerWrapperStyle}>
        <ButtonsContainer className={buttonsContainerStyle}>
          <ButtonsRow>
            <Button
              style="secondary"
              text={t(i18nKeys.cancelRecover)}
              onClick={this.handleCancelClick}
            />

            <Button
              className="login-button"
              text={t(i18nKeys.sendRecoveryEmail)}
              style="primary"
              done={success}
              doneText={t(i18nKeys.requestPasswordRecoverySent)}
              onClick={() => {}}
              disabled={!email}
              htmlType="submit"
            />
          </ButtonsRow>
        </ButtonsContainer>
      </div>
    );
  }
}

const buttonsContainerStyle = style({
  width: 240
});

const buttonsContainerWrapperStyle = style({
  display: "flex",
  width: "100%",
  justifyContent: "end"
});

const successButtonContainerStyle = style({
  width: "fit-content"
});

const successTextContainerStyle = style({
  paddingTop: 12
});
